import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qk } from '@client/lib/api';
import { useOutbox, type OutboxEmail } from '@client/lib/admin-api';
import { PageHeader } from '@client/components/admin/shared';
import { Button } from '@client/components/ui/button';
import { Sheet } from '@client/components/ui/overlay';
import { Select } from '@client/components/ui/input';
import { Spinner } from '@client/components/ui/feedback';
import { Badge } from '@client/components/ui/badge';
import { Table, Td, Th, Tr } from '@client/components/ui/table';
import { useToast } from '@client/components/ui/toast';
import { fmtDateTime } from '@client/lib/format';

export const Route = createFileRoute('/admin/outbox')({ component: OutboxPage });

function OutboxPage() {
  const [status, setStatus] = React.useState('');
  const outbox = useOutbox(status || undefined);
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = React.useState<OutboxEmail | null>(null);
  const [copy, setCopy] = React.useState<'main' | 'bcc'>('main');
  const resend = useMutation({ mutationFn: (id: string) => api<{ ok: boolean; result: { status: string; error?: string } }>(`/api/admin/outbox/${id}/resend`, { method: 'POST' }), onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['admin', 'outbox'] }); toast.push({ tone: r.ok ? 'good' : 'critical', title: r.ok ? 'Sent' : `Not sent: ${r.result.error ?? r.result.status}` }); } });
  const tone = (s: string) => (s === 'sent' ? 'good' : s === 'failed' ? 'critical' : s === 'held' ? 'warning' : 'neutral');
  return (
    <>
      <PageHeader title="Emails" eyebrow="Outbox · every email, with its BCC copy" actions={<Select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 w-40 text-[13px]"><option value="">All</option><option value="queued">Queued</option><option value="sent">Sent</option><option value="held">Held</option><option value="failed">Failed</option></Select>} />
      <div className="px-6 py-6 md:px-8">
        {outbox.data && !outbox.data.smtp_configured ? <p className="mb-4 rounded-card border border-warning-bg bg-warning-bg px-4 py-3 text-[13px] text-warning">SMTP is not configured. Emails are held here; open one to read it (including sign-in links) and use "Resend" once SMTP is set up.</p> : null}
        {outbox.isPending ? <Spinner /> : (
          <div className="rounded-card border border-border bg-card">
            <Table>
              <thead><tr><Th>When</Th><Th>To</Th><Th>Template</Th><Th>Subject</Th><Th>Status</Th><Th>BCC</Th><Th /></tr></thead>
              <tbody>
                {outbox.data?.emails.map((e) => (
                  <Tr key={e.id}>
                    <Td className="whitespace-nowrap text-muted">{fmtDateTime(e.createdAt, 'en')}</Td>
                    <Td>{e.recipient}</Td>
                    <Td><Badge tone="outline">{e.template}</Badge> <span className="text-muted">{e.locale}</span></Td>
                    <Td className="max-w-[320px] truncate"><button className="text-brand" onClick={() => { setOpen(e); setCopy('main'); }}>{e.subject}</button></Td>
                    <Td><Badge tone={tone(e.status)}>{e.status}</Badge>{e.lastError ? <p className="mt-0.5 max-w-[240px] truncate text-[11.5px] text-critical" title={e.lastError}>{e.lastError}</p> : null}</Td>
                    <Td className="text-muted">{e.bcc ? `${e.bcc}${e.bccSentAt ? ' ✓' : ''}` : '—'}</Td>
                    <Td className="text-right">{e.status !== 'sent' ? <Button size="sm" variant="ghost" onClick={() => resend.mutate(e.id)}>Resend</Button> : null}</Td>
                  </Tr>
                ))}
                {outbox.data?.emails.length === 0 ? <tr><Td colSpan={7} className="py-8 text-center text-muted">No emails.</Td></tr> : null}
              </tbody>
            </Table>
          </div>
        )}
      </div>
      {open ? (
        <Sheet open onOpenChange={(o) => !o && setOpen(null)} title={open.subject} description={`${open.template} → ${open.recipient} · ${open.status}`} width="max-w-[720px]">
          {open.bcc ? (
            <div className="mb-3 inline-flex rounded-btn border border-border p-0.5 text-[13px] font-[570]">
              <button onClick={() => setCopy('main')} className={`rounded-[8px] px-3 py-1 ${copy === 'main' ? 'bg-ink text-white' : 'text-muted'}`}>Investor copy</button>
              <button onClick={() => setCopy('bcc')} className={`rounded-[8px] px-3 py-1 ${copy === 'bcc' ? 'bg-ink text-white' : 'text-muted'}`}>BCC copy ({open.bcc})</button>
            </div>
          ) : null}
          <iframe title="email" src={`/api/admin/outbox/${open.id}/html?copy=${copy}`} className="h-[70vh] w-full rounded-card border border-border bg-surface" sandbox="allow-same-origin" />
        </Sheet>
      ) : null}
    </>
  );
}
